<template>
    <form-wrapper @submit="handleFormSubmission(data)">
        <practice-fragment
            v-if="showPracticeNameAndTypeFields"
            :isPracticeSuggestable="false"
            :isDisabled="isUserAlreadyInPractice || isSending"
            :formData="data"
            @change="change($event)"
        ></practice-fragment>

        <form-row v-if="!$auth.isUserPracticeOther() && !$auth.wasUserDemo()">
            <form-cell :class="isMobileLarge ? 'auth-signup-practice__insurance' : ''">
                <p class="auth-signup-practice__insurance--label">
                    {{ $t('forms.practice.account_information.insurance_plans.label') }}
                </p>
                <common-button
                    :isSubmittable="false"
                    :label="
                        insurancePlans.length
                            ? $t('forms.practice.account_information.buttons.modify_insurances')
                            : $t('forms.practice.account_information.buttons.select_insurances')
                    "
                    type="primary"
                    size="tiny"
                    class="auth-signup-practice__insurance--button"
                    :isDisabled="isSending"
                    @click="toggleAlphabetTable"
                ></common-button>
            </form-cell>
        </form-row>
        <form-row v-if="insurancePlans.length && !$auth.isUserPracticeOther() && !$auth.wasUserDemo()">
            <form-cell>
                <form-cell width="four-fifths">
                    <div class="auth-signup-practice__overflow-y">
                        <common-badges-list ref="badgeList" :listElements="insurancePlansNames"></common-badges-list>
                    </div>
                </form-cell>
            </form-cell>
        </form-row>
        <form-row v-if="showInsuranceWarning && !insurancePlans.length">
            <form-cell>
                <common-warning size="medium" :text="warningInsuranceMessage" :isCenter="true"></common-warning>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell>
                <text-field
                    :label="emailLabel"
                    :placeholder="emailPlaceholder"
                    :hint="isTabletSmall ? '' : $t('forms.auth.practice.fields.practice_email.hint')"
                    :formData="data"
                    :isRequired="!isUserAlreadyInPractice"
                    :isDisabled="isUserAlreadyInPractice || isSending"
                    valuePath="email"
                    type="email"
                    @change="change($event)"
                ></text-field>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell>
                <locations-fragment
                    :facilityServices="facilityServices"
                    :states="states"
                    :isRequired="!isUserAlreadyInPractice"
                    :isDisabled="isUserAlreadyInPractice || isSending"
                    :formData="data"
                    :hasAutofocus="true"
                    valuePath="locations"
                    @change="change($event)"
                ></locations-fragment>
            </form-cell>
        </form-row>
        <form-row v-if="$auth.isUserTemporaryPhysician()">
            <form-cell>
                <phone-field
                    :label="$t('forms.referrals.make.fields.physician_phone_number.label')"
                    :placeholder="$t('common.phone_number.placeholder')"
                    :isRequired="true"
                    :isDisabled="isUserAlreadyInPractice || isSending"
                    :formData="data"
                    valuePath="phone"
                    @change="change($event)"
                >
                    <template #hint>
                        <p v-html="$t('forms.auth.register.fields.phone.mobile_sms_hint_secondary')"></p>
                    </template>
                </phone-field>
            </form-cell>
        </form-row>

        <template #footer>
            <common-content-spacer height="medium">
                <common-buttons>
                    <common-button :isProcessing="isSending" :label="$t('common.microcopy.next')" type="primary"></common-button>
                </common-buttons>
                <div class="auth-signup-practice__footer-message">
                    <div>
                        {{ $t('forms.auth.practice.footer.message') }}
                    </div>
                    <div v-if="!isTabletSmall" class="auth-signup-practice__footer-hint">
                        <common-info-tip :isExpanded="true">
                            <span>{{ $t('forms.auth.practice.footer.hint') }}</span>
                        </common-info-tip>
                    </div>
                </div>
            </common-content-spacer>
        </template>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import PracticesAPI from 'api/PracticesAPI'
    import PracticeToFormDataTransformer from 'transformers/PracticeToFormDataTransformer'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import TemporaryReferralsAPI from 'api/TemporaryReferralsAPI'

    export default {
        mixins: [FormMixin, ScreenSizeDetectionMixin],

        props: {
            practice: { type: Object, default: undefined },
            insurancePlans: { type: Array, required: true },
            facilityServices: { type: Array, required: true },
            states: { type: Array, required: true },
        },

        data() {
            const formData = PracticeToFormDataTransformer.transform(this.practice)

            return {
                data: {
                    ...formData,
                    practiceType: formData.practiceType || this.$auth.getUser().practiceType || null,
                    name: formData.name || this.$auth.getUser().practiceName,
                },
                warningInsuranceMessage: 'At least one insurance plan is required',
                showInsuranceWarning: false,
                isSending: false,
            }
        },

        computed: {
            showPracticeNameAndTypeFields() {
                if (this.isUserAlreadyInPractice) {
                    return false
                }

                return this.$auth.getUser().practiceType === null || this.$auth.getUser().practiceName === null
            },

            insurancePlansNames() {
                return this.insurancePlans.map((item) => item.name)
            },

            isUserAlreadyInPractice() {
                return Boolean(this.practice.id)
            },

            emailLabel() {
                return this.$auth.getUser().practiceType !== PracticeTypeEnum.ANCILLARY
                    ? this.$t('forms.auth.practice.fields.practice_email.label')
                    : this.$t('forms.auth.practice.fields.ancillary_email.label')
            },

            emailPlaceholder() {
                return this.$auth.getUser().practiceType !== PracticeTypeEnum.ANCILLARY
                    ? this.$t('forms.auth.practice.fields.practice_email.placeholder')
                    : this.$t('forms.auth.practice.fields.ancillary_email.placeholder')
            },
        },

        methods: {
            handleFormSubmission(data) {
                const isTemporaryPhysician = this.$auth.isUserTemporaryPhysician()
                this.isSending = true
                this.showInsuranceWarning = false
                if (!this.isUserAlreadyInPractice) {
                    if (
                        !this.insurancePlans.length &&
                        !this.$auth.isUserPracticeOther() &&
                        !this.$auth.wasUserDemo() &&
                        !isTemporaryPhysician &&
                        this.$auth.getUser().practiceType !== PracticeTypeEnum.ANCILLARY
                    ) {
                        this.showInsuranceWarning = true
                        this.isSending = false
                        return
                    }
                    data.insurancePlanIds = this.insurancePlans.map((item) => item.id)
                    return this.handleRequest(PracticesAPI.create(data)).then(() => {
                        if (isTemporaryPhysician) {
                            return TemporaryReferralsAPI.createReferralsPool().then(() => {
                                this.emitSent()
                            })
                        }

                        this.emitSent()
                    })
                }
                return this.emitSent()
            },

            toggleAlphabetTable() {
                this.emitEvent({ name: 'toggle-alphabet-table' })
            },
        },

        watch: {
            insurancePlansNames(newValue, oldValue) {
                if (newValue !== oldValue) {
                    if (this.$refs.badgeList) {
                        this.$refs.badgeList.calculateMore()
                    }
                }
            },
        },

        mounted() {
            // Proxy-change 'name' attribute of practice every time 'practiceName' in the fragment
            // changes. Attribute name in the fragment is set to 'practiceName' as main purpose of
            // the fragment is to be used on Register page where such attribute name is used.
            this.$on('local:afterChange:practiceName', ({ value }) => this.change({ path: 'name', value }))
        },
    }
</script>
<style lang="scss" scoped>
    @import 'styles/setup/_variables.scss';
    .auth-signup-practice {
        &__footer-message {
            display: flex;
            align-items: center;
            text-align: center;
        }
        &__footer-hint {
            width: 40px;
        }
        &__overflow-y {
            min-height: 60px;
            max-height: 300px;
            overflow-y: auto;
        }
        &__insurance {
            text-align: center;
        }
        &__insurance--button {
            margin-top: 10px;
            height: 50px;
            width: 190px;
        }
        &__insurance--label {
            font-weight: 500;
            font-size: 14px;
            line-height: 21px;
            letter-spacing: 0.175px;
            color: $color--txt-base-6;
        }
    }
</style>
